import React from 'react';
import { useNavigate } from 'react-router-dom';

const ExperienceJourney: React.FC = () => {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate('/contact');
  };

  return (
    <div className="bg-soft-cream">
      {/* Your Vision Section */}
      <section id="vision" className="py-8 sm:py-10 md:py-16 lg:py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 lg:gap-12 items-center">
            <div className="order-2 sm:order-1">
              <img
                src="/photos/action1.jpg"
                alt="Bride getting styled"
                className="w-full max-w-[150px] sm:max-w-[200px] md:max-w-[250px] lg:max-w-[300px] xl:max-w-[350px] mx-auto object-contain rounded-lg shadow-xl image-hover"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="font-serif text-3xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-deep-teal mb-3 sm:mb-4 md:mb-6">
                Your Vision, Brought to Life
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-3 sm:mb-4 md:mb-6 leading-relaxed">
                Like the gentle trade winds that caress Kauai's shores, your beauty should feel 
                natural, effortless, and authentically you. I understand that your wedding day 
                isn't just about looking beautiful. It's about feeling radiant, confident, and 
                completely yourself as you say "I do" in paradise.
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-4 sm:mb-6 md:mb-8 leading-relaxed">
                Every bride deserves to feel stunning while staying true to her essence. 
                Together, we'll create a look that makes you glow from within, capturing 
                both your natural beauty and the magic of your Kauai wedding day.
              </p>
              <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-md">
                <h3 className="font-semibold text-deep-teal mb-2 sm:mb-3 text-xs sm:text-sm md:text-base">Addressing Your Concerns:</h3>
                <p className="text-gray-600 italic text-xs sm:text-sm md:text-base">
                  "I want to look beautiful but still like myself."
                </p>
                <p className="text-gray-700 mt-2 text-xs sm:text-sm md:text-base">
                  My philosophy is enhancement, not transformation. We'll work together to 
                  amplify your natural features while ensuring you feel completely comfortable 
                  and authentically beautiful.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stress-Free Process Section */}
      <section id="process" className="py-8 sm:py-12 md:py-16 lg:py-12 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 items-center">
            <div>
              <h2 className="font-serif  text-3xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-deep-teal mb-3 sm:mb-4 md:mb-6">
                A Stress-Free Process
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-3 sm:mb-4 md:mb-6 leading-relaxed">
                Your wedding morning should be filled with joy and anticipation, not worry 
                about timing or whether your look will hold up in Kauai's tropical climate. 
                I handle every detail of your beauty timeline, so you can focus on what 
                truly matters, celebrating your love story.
              </p>
              <div className="space-y-2 sm:space-y-3 md:space-y-4 mb-4 sm:mb-6 md:mb-8">
                <div className="flex items-start space-x-3">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-island-sage rounded-full flex-shrink-0 mt-1"></div>
                  <p className="text-gray-700 text-xs sm:text-sm md:text-base">Complete timeline planning from start to finish</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-island-sage rounded-full flex-shrink-0 mt-1"></div>
                  <p className="text-gray-700 text-xs sm:text-sm md:text-base">Coordination with your photographer and venue</p>
                </div>
              </div>
              <div className="bg-warm-sand p-3 sm:p-4 md:p-6 rounded-lg">
                <h3 className="font-semibold text-deep-teal mb-2 sm:mb-3 text-xs sm:text-sm md:text-base">Peace of Mind:</h3>
                <p className="text-gray-600 italic mb-2 text-xs sm:text-sm md:text-base">
                  "I'm worried about timing on the wedding day."
                </p>
                <p className="text-gray-700 text-xs sm:text-sm md:text-base">
                  With 15+ years of experience, I've perfected the art of wedding day timing. 
                  I arrive early, work efficiently, and always have contingency plans to 
                  ensure everything runs smoothly.
                </p>
              </div>
            </div>
            <div>
              <img
                src="/photos/main2.jpg"
                alt="Peaceful bridal preparation"
                className="w-full h-full object-cover rounded-lg shadow-xl image-hover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Personalized Plan Section */}
      <section id="plan" className="py-8 sm:py-16 lg:py-10 px-4 sm:px-6 bg-soft-cream">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-deep-teal mb-8 sm:mb-10 lg:mb-12">
            Your Personalized Hair & Makeup Plan
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-12 md:mb-14 lg:mb-16">
            <div className="bg-white p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg shadow-lg text-center">
              <div className="plan-step-mobile flex items-center justify-center gap-3 mb-3 md:mb-4">
                <div className="plan-number-container">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-sunset-coral rounded-full flex items-center justify-center">
                    <span className="text-xs sm:text-sm md:text-base text-white font-bold">1</span>
                  </div>
                </div>
                <h3 className="font-serif text-base sm:text-lg md:text-xl lg:text-2xl text-deep-teal">Online Consultation</h3>
              </div>
              <p className="text-gray-700 text-xs sm:text-sm md:text-base">
                We'll discuss your vision, review inspiration photos, and plan your 
                perfect look via video.
              </p>
            </div>
            
            <div className="bg-white p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg shadow-lg text-center">
              <div className="plan-step-mobile flex items-center justify-center gap-3 mb-3 md:mb-4">
                <div className="plan-number-container">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-island-sage rounded-full flex items-center justify-center">
                    <span className="text-xs sm:text-sm md:text-base text-white font-bold">2</span>
                  </div>
                </div>
                <h3 className="font-serif text-base sm:text-lg md:text-xl lg:text-2xl text-deep-teal">Trial Runs</h3>
              </div>
              <p className="text-gray-700 text-xs sm:text-sm md:text-base">
                1 practice session to perfect your look, test longevity, and 
                ensure you're completely confident and comfortable.
              </p>
            </div>
            
            <div className="bg-white p-3 sm:p-4 md:p-6 lg:p-8 rounded-lg shadow-lg text-center">
              <div className="plan-step-mobile flex items-center justify-center gap-3 mb-3 md:mb-4">
                <div className="plan-number-container">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 bg-ocean-blue rounded-full flex items-center justify-center">
                    <span className="text-xs sm:text-sm md:text-base text-white font-bold">3</span>
                  </div>
                </div>
                <h3 className="font-serif text-base sm:text-lg md:text-xl lg:text-2xl text-deep-teal">Wedding Day Magic</h3>
              </div>
              <p className="text-gray-700 text-xs sm:text-sm md:text-base">
                Flawless execution of your perfected look, plus touch-ups throughout 
                the day to keep you looking radiant from ceremony to reception.
              </p>
            </div>
          </div>

          <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-lg max-w-2xl mx-auto">
            <h3 className="font-semibold text-deep-teal mb-2 sm:mb-3 text-sm sm:text-base">For Out-of-State Brides:</h3>
            <p className="text-gray-600 italic mb-2 text-sm sm:text-base">
              "I don't live on Kauai—how do we coordinate?"
            </p>
            <p className="text-gray-700 text-sm sm:text-base">
              Distance is no barrier to perfection! I work with brides worldwide through 
              detailed video consultations and comprehensive planning. <br/>
              Many of my brides arrive just days before their wedding feeling completely 
              prepared and confident.
            </p>
          </div>
        </div>
      </section>

      {/* Island Expertise Section */}
      <section id="expertise" className="py-9 sm:py-20 lg:py-10 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="font-serif text-3xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-deep-teal mb-4 sm:mb-6">
              A True Island Expert
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Born and raised on Kauai, I understand the unique challenges and incredible 
              beauty of island weddings. Your look will be designed to thrive in our 
              tropical paradise.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-10 lg:mb-12">
            <div className="text-center">
              <img
                src="/photos/makeup-action-1.jpeg"
                alt="Humidity-proof makeup application"
                className="expertise-image expertise-image-1 image-hover mx-auto mb-3 sm:mb-4 md:mb-6"
              />
              <h3 className="font-serif text-base sm:text-lg md:text-xl lg:text-2xl text-deep-teal mb-2 sm:mb-3">Humidity-Proof Application</h3>
              <p className="text-gray-700 text-xs sm:text-sm md:text-base px-2 sm:px-0">
                Specialized techniques and products that withstand Kauai's tropical climate, 
                ensuring your makeup stays flawless from sunrise to sunset.
              </p>
            </div>

            <div className="text-center">
              <img
                src="/photos/makeup-action-2.jpeg"
                alt="Wind-safe hairstyling"
                className="expertise-image expertise-image-2 image-hover mx-auto mb-3 sm:mb-4 md:mb-6"
              />
              <h3 className="font-serif text-base sm:text-lg md:text-xl lg:text-2xl text-deep-teal mb-2 sm:mb-3">Wind-Safe Hairstyles</h3>
              <p className="text-gray-700 text-xs sm:text-sm md:text-base px-2 sm:px-0">
                Elegant styles that look stunning and stay secure, whether you're saying 
                vows on a clifftop or dancing under the stars on the beach.
              </p>
            </div>

            <div className="text-center">
              <img
                src="/photos/makeup-action-3.jpeg"
                alt="Local venue expertise"
                className="expertise-image expertise-image-3 image-hover mx-auto mb-3 sm:mb-4 md:mb-6"
              />
              <h3 className="font-serif text-base sm:text-lg md:text-xl lg:text-2xl text-deep-teal mb-2 sm:mb-3">Local Venue Familiarity</h3>
              <p className="text-gray-700 text-xs sm:text-sm md:text-base px-2 sm:px-0">
                Intimate knowledge of Kauai's wedding venues, lighting conditions, and 
                logistics to ensure perfect timing and flawless results.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Free Consultation Section */}
      <section id="consultation" className="py-8 sm:py-12 md:py-16 lg:py-12 px-4 sm:px-6 bg-deep-teal text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl mb-4 sm:mb-6">
            FREE Bridal Hair & Makeup Consultation
          </h2>
          
          <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 md:mb-10 leading-relaxed">
            Let's discuss your vision and create the perfect plan for your wedding day beauty. 
            This complimentary consultation helps us plan every detail of your bridal look.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">
            <div className="bg-white bg-opacity-10 p-3 sm:p-4 md:p-6 rounded-lg">
              <h3 className="font-serif text-lg sm:text-xl lg:text-2xl mb-3 sm:mb-4">What We'll Discuss:</h3>
              <ul className="text-left space-y-2 sm:space-y-3 text-sm sm:text-base">
                <li className="flex items-start space-x-3">
                  <span className="text-sunset-coral">✓</span>
                  <span>Your vision and inspiration for your wedding day look</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-sunset-coral">✓</span>
                  <span>Hair and makeup style recommendations</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-sunset-coral">✓</span>
                  <span>Timeline planning for your wedding day</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-sunset-coral">✓</span>
                  <span>Service packages and pricing options</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-sunset-coral">✓</span>
                  <span>Trial session planning and preparation tips</span>
                </li>
              </ul>
            </div>

            <div className="bg-white bg-opacity-10 p-4 sm:p-6 rounded-lg">
              <h3 className="font-serif text-lg sm:text-xl lg:text-2xl mb-3 sm:mb-4">Perfect For Brides Who:</h3>
              <ul className="text-left space-y-2 sm:space-y-3 text-sm sm:text-base">
                <li className="flex items-start space-x-3">
                  <span className="text-sunset-coral">•</span>
                  <span>Want to ensure their hair is wedding-ready</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-sunset-coral">•</span>
                  <span>Need guidance on Kauai wedding hair planning</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-sunset-coral">•</span>
                  <span>Are considering extensions or major style changes</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-sunset-coral">•</span>
                  <span>Want professional advice on their vision</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="text-sunset-coral">•</span>
                  <span>Value expert preparation and planning</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white bg-opacity-10 p-4 sm:p-6 rounded-lg mb-6 sm:mb-8">
            <h3 className="font-semibold mb-2 sm:mb-3 text-sm sm:text-base">Understanding Your Vision:</h3>
            <p className="text-gray-200 italic mb-2 text-sm sm:text-base">
              "How do I know she understands my vision?"
            </p>
            <p className="text-gray-100 text-sm sm:text-base">
              This consultation is all about you! We'll review your inspiration photos, 
              discuss your dress and venue, and ensure I completely understand your dream 
              look before we even begin. Your vision guides everything we do together.
            </p>
          </div>

          <button
            onClick={handleContactClick}
            className="inline-block bg-sunset-coral hover:bg-opacity-90 text-white font-semibold py-3 px-6 sm:py-4 sm:px-8 lg:px-12 rounded-full text-sm sm:text-base lg:text-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Contact Us
          </button>
          
          <p className="mt-3 sm:mt-4 text-gray-300 text-sm sm:text-base">
            Available via video call or in-person • No obligation • Completely complimentary
          </p>
        </div>
      </section>
    </div>
  );
};

export default ExperienceJourney;
